import {
    Menu,
    MenuItem,
    Checkbox,
    ClickAwayListener,
    Button,
    Box,
    Typography,
    CircularProgress,
    Chip,
    Stack,
    Popover,
    Divider,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CloseIcon from '@mui/icons-material/Close';
import { FixedSizeList } from 'react-window';
import { useMemo, useState, useEffect, useRef } from 'react';
import { SearchBar } from './SearchBar';
import { useStyles } from './styles/MultiSelectFilter.styles';

const ITEM_HEIGHT = 40;

type MultiSelectValue = string | number | object;

interface MultiSelectFilterProps<Value extends MultiSelectValue> {
    label: string;
    idKey?: string;
    disabledWithClear?: boolean;
    onClear?: () => void;
    options: Value[];
    value: Record<string, boolean>;
    onChange: (id: Value) => void;
    getLabelValue: (option: Value) => string;
    disabled?: boolean;
    sort?: boolean;
    margin?: boolean;
    showChips?: boolean;
    search?: boolean;
}

export const MultiSelectFilter = <Value extends MultiSelectValue>({
    label,
    idKey,
    disabled,
    disabledWithClear,
    onClear,
    search,
    options,
    value,
    onChange,
    getLabelValue,
    sort = true,
    margin = true,
    showChips = true,
}: MultiSelectFilterProps<Value>) => {
    const classes = useStyles();
    const [dropdownAnchor, setDropdownAnchor] = useState<HTMLElement | null>(null);
    const [searchText, setSearchText] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [displayOptions, setDisplayOptions] = useState<Value[]>([]);
    const [menuWidth, setMenuWidth] = useState(250);
    const [menuHeight, setMenuHeight] = useState(400);
    const menuRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<FixedSizeList>(null);
    const [chipAnchor, setChipAnchor] = useState<HTMLElement | null>(null);

    const selected = useMemo(
        () => options.filter((option: any) => value[idKey ? (option as any)[idKey] : option]),
        [options, value, idKey]
    );

    const filteredOptions = useMemo(() => {
        const text = searchText.toLowerCase().trim();
        if (!text) return options;
        return options.filter((option: any) => getLabelValue(option).toLowerCase().includes(text));
    }, [searchText, options, getLabelValue]);

    const sortedOptions = useMemo(() => {
        if (!sort) return filteredOptions;
        return [...filteredOptions].sort((a, b) => {
            const aSelected = value[idKey ? (a as any)[idKey] : a] ? 1 : 0;
            const bSelected = value[idKey ? (b as any)[idKey] : b] ? 1 : 0;
            if (bSelected !== aSelected) return bSelected - aSelected;
            return getLabelValue(a).localeCompare(getLabelValue(b));
        });
    }, [filteredOptions, value, idKey, getLabelValue, sort]);

    useEffect(() => {
        if (dropdownAnchor) {
            setIsLoading(true);
            const anchorWidth = dropdownAnchor.getBoundingClientRect().width;
            setMenuWidth(Math.max(300, anchorWidth));
            requestAnimationFrame(() => {
                setDisplayOptions(sortedOptions);
                setIsLoading(false);
                (listRef.current as any)?.resetAfterIndex?.(0);
            });
        } else {
            setSearchText('');
        }
    }, [dropdownAnchor, sortedOptions]);

    useEffect(() => {
        if (!isLoading) {
            const itemCount = displayOptions.length;
            const newHeight = Math.min(400, itemCount * (ITEM_HEIGHT + 20));
            setMenuHeight(newHeight);
        }
    }, [displayOptions, isLoading]);

    // Forward the entire option object to the parent so consumers can access additional fields (e.g. companyId, modelNumber)
    const handleCheckboxChange = (option: Value) => onChange(option);

    const ItemRenderer = ({ index, style }: { index: number; style: React.CSSProperties }) => {
        const option = displayOptions[index];
        const optionId = idKey ? (option as any)[idKey] : option;
        const checked = Boolean(value[optionId]);
        return (
            <MenuItem
                onClick={() => handleCheckboxChange(option as Value)}
                style={{ ...style, padding: 0 }}
                divider
                disableRipple>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', px: 2, py: 1 }}>
                    <Checkbox
                        size='small'
                        disableRipple
                        checked={checked}
                        onChange={() => handleCheckboxChange(option as Value)}
                        onClick={(e) => e.stopPropagation()}
                    />
                    <Typography noWrap>{getLabelValue(option)}</Typography>
                </Box>
            </MenuItem>
        );
    };

    return (
        <Box ml={0}>
            <Stack spacing={0.5}>
                <Button
                    variant='outlined'
                    size='small'
                    disabled={disabled && !disabledWithClear}
                    onClick={(e) => {
                        if (disabledWithClear) onClear?.();
                        setDropdownAnchor(dropdownAnchor ? null : e.currentTarget);
                    }}
                    onMouseEnter={(e) => setChipAnchor(e.currentTarget)}
                    onMouseLeave={(e) => {
                        const related = e.relatedTarget as HTMLElement;
                        if (!related?.closest('.chip-hover-bridge, .chip-popover')) setChipAnchor(null);
                    }}
                    endIcon={<KeyboardArrowDownIcon />}
                    sx={{
                        textTransform: 'none',
                        height: 36,
                        fontSize: '0.875rem',
                        px: 2,
                        color: 'grey.800',
                        borderColor: 'grey.300',
                    }}>
                    {label} {selected.length > 0 && `(${selected.length})`}
                </Button>

                {Boolean(chipAnchor) && (
                    <Box
                        className='chip-hover-bridge'
                        sx={{
                            position: 'absolute',
                            bottom: -8,
                            left: 0,
                            right: 0,
                            height: 8,
                            zIndex: 1300,
                        }}
                        onMouseEnter={() => chipAnchor && setChipAnchor(chipAnchor)}
                    />
                )}

                {showChips && selected.length > 0 && (
                    <Popover
                        open={Boolean(chipAnchor)}
                        anchorEl={chipAnchor}
                        sx={{
                            pointerEvents: 'none',
                            '& .MuiPaper-root': {
                                pointerEvents: 'auto',
                            },
                        }}
                        onClose={() => setChipAnchor(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                        PaperProps={{
                            className: 'chip-popover',
                            onMouseLeave: (e: React.MouseEvent<HTMLDivElement>) => {
                                const related = e.relatedTarget as HTMLElement;
                                if (!related?.closest('.chip-hover-bridge, .MuiButton-root')) setChipAnchor(null);
                            },
                            sx: {
                                pointerEvents: 'auto',
                                mt: 0,
                                p: 1,
                                maxWidth: Math.min(menuWidth, 190),
                                maxHeight: 200,
                                overflow: 'auto',
                                width: '100%',
                            },
                        }}>
                        <Stack spacing={1} divider={<Divider flexItem />} sx={{ pl: 1.5, pr: 1.5 }}>
                            {selected.map((option: any, idx: number) => {
                                const optionId = idKey ? (option as any)[idKey] : option;
                                return (
                                    <Chip
                                        key={`chip-${optionId}-${idx}`}
                                        label={getLabelValue(option)}
                                        size='small'
                                        color='primary'
                                        variant='outlined'
                                        onDelete={() => {
                                            handleCheckboxChange(option as Value);
                                        }}
                                        deleteIcon={<CloseIcon fontSize='small' />}
                                    />
                                );
                            })}
                        </Stack>
                    </Popover>
                )}
            </Stack>

            <Menu
                open={Boolean(dropdownAnchor) && !disabledWithClear}
                anchorEl={dropdownAnchor}
                onClose={() => setDropdownAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{ sx: { width: menuWidth, maxHeight: 500 } }}
                MenuListProps={{ style: { padding: 0 }, ref: menuRef as any }}>
                <ClickAwayListener onClickAway={() => setDropdownAnchor(null)}>
                    <Box sx={{ width: '100%' }}>
                        {search && (
                            <Box
                                p={1}
                                sx={{
                                    position: 'sticky',
                                    top: 0,
                                    bgcolor: 'background.paper',
                                    zIndex: 1,
                                }}>
                                <SearchBar width={menuWidth - 20} value={searchText} onSearch={setSearchText} />
                            </Box>
                        )}

                        {isLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2, height: 100 }}>
                                <CircularProgress size={24} />
                            </Box>
                        ) : displayOptions.length === 0 ? (
                            <MenuItem sx={{ justifyContent: 'center', height: 40 }} disableRipple>
                                No Items Found
                            </MenuItem>
                        ) : (
                            <Box sx={{ height: menuHeight, width: '100%' }}>
                                {displayOptions.length <= 5 ? (
                                    displayOptions.map((option, index) => {
                                        const optionId = idKey ? (option as any)[idKey] : option;
                                        const isChecked = Boolean(value[optionId]);
                                        return (
                                            <MenuItem
                                                key={`menu-item-${optionId}-${index}`}
                                                onClick={() => handleCheckboxChange(option as Value)}
                                                divider
                                                disableRipple
                                                sx={{ padding: 0 }}>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        px: 2,
                                                        py: 1,
                                                        width: '100%',
                                                    }}>
                                                    <Checkbox
                                                        size='small'
                                                        disableRipple
                                                        checked={isChecked}
                                                        onChange={() => handleCheckboxChange(option as Value)}
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                    <Typography noWrap>{getLabelValue(option)}</Typography>
                                                </Box>
                                            </MenuItem>
                                        );
                                    })
                                ) : (
                                    <FixedSizeList
                                        ref={listRef}
                                        height={menuHeight}
                                        width='100%'
                                        itemCount={displayOptions.length}
                                        itemSize={ITEM_HEIGHT}
                                        overscanCount={5}
                                        style={{ overflowX: 'hidden' }}>
                                        {ItemRenderer}
                                    </FixedSizeList>
                                )}
                            </Box>
                        )}
                    </Box>
                </ClickAwayListener>
            </Menu>
        </Box>
    );
};
